import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const CustomerEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/customers",
    token
  );
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const currencies = Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || [];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [openingBalanceCurrency, setOpeningBalanceCurrency] = useState("");

  useEffect(() => {
    if (editData) {
      setName(editData.name || "");
      setEmail(editData.email || "");
      setContact(editData.contact || "");
      setAddress(editData.address || "");
      const shopId =
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : "";
      setSelectedShop(shopId);
      setOpeningBalance(
        editData.opening_balance != null ? String(editData.opening_balance) : ""
      );
      const currId =
        editData.opening_balance_currency_details?.id ??
        editData.opening_balance_currency;
      setOpeningBalanceCurrency(currId ? String(currId) : "");
    }
  }, [editData]);

  // Once shops are loaded, ensure selectedShop is valid.
  useEffect(() => {
    if (editData && shops && shops.length > 0) {
      const shopId =
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : "";
      const exists = (shops || []).find((s) => String(s.id) === shopId);
      if (exists) {
        setSelectedShop(shopId);
      } else {
        setSelectedShop("");
      }
    }
  }, [editData, shops]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("email", email.trim());
    formData.append("contact", contact.trim());
    formData.append("address", address.trim());
    formData.append("shop", selectedShop);
    formData.append(
      "opening_balance",
      openingBalance === "" ? "0" : openingBalance
    );
    if (openingBalanceCurrency)
      formData.append("opening_balance_currency", openingBalanceCurrency);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setName("");
      setEmail("");
      setContact("");
      setAddress("");
      setSelectedShop("");
      setOpeningBalance("");
      setOpeningBalanceCurrency("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("customer.editCustomer")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("customer.customerName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("customer.customerEmail")}
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("customer.customerContact")}
            type="text"
            fullWidth
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("customer.customerAddress")}
            type="text"
            fullWidth
            multiline
            rows={3}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t(
              "customer.openingBalance",
              "Opening balance (existing dues)"
            )}
            type="number"
            fullWidth
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
          />
          <TextField
            select
            margin="dense"
            label={t(
              "customer.openingBalanceCurrency",
              "Opening balance currency"
            )}
            fullWidth
            value={openingBalanceCurrency}
            onChange={(e) => setOpeningBalanceCurrency(e.target.value)}
            disabled={currencyLoading}
          >
            <MenuItem value="">{t("customer.none", "None")}</MenuItem>
            {(currencies || []).map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.title}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("customer.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("customer.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim() || !selectedShop}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("customer.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerEdit;
